import { type ReactElement, useState, useContext, useCallback, useEffect, useMemo } from 'react'
import { Box, Button, CardActions, CircularProgress, Divider, Typography, Alert } from '@mui/material'
import Stack from '@mui/system/Stack'
import CheckWallet from '@/components/common/CheckWallet'
import { SafeTxContext } from '@/components/tx-flow/SafeTxProvider'
import { TxModalContext } from '@/components/tx-flow'
import commonCss from '@/components/tx-flow/common/styles.module.css'
import type { SignOrExecuteProps } from './SignOrExecuteFormV2'
import { useHederaTxActions } from '@/hooks/hedera/useHederaTxActions'
import { useHederaGas } from '@/hooks/hedera/useHederaGas'
import { useHederaApprovalStatus } from '@/hooks/hedera/useHederaApprovalStatus'
import useIsSafeOwner from '@/hooks/useIsSafeOwner'
import { trackError, Errors } from '@/services/exceptions'
import { asError } from '@/services/exceptions/utils'
import { isWalletRejection } from '@/utils/wallets'
import { getAndValidateSafeSDK } from '@/services/tx/tx-sender/sdk'
import WalletRejectionError from './WalletRejectionError'
import ErrorMessage from '../ErrorMessage'
import { SuccessScreenFlow, ApproveHashSuccessScreenFlow } from '@/components/tx-flow/flows'

type ApprovalStep = 'propose' | 'approve' | 'execute' | 'done'

export const HederaApproveExecuteForm = ({
  txId: existingTxId,
  onSubmit,
  disableSubmit = false,
  origin,
  isCreation,
}: SignOrExecuteProps): ReactElement => {
  // Form state
  const [isSubmittable, setIsSubmittable] = useState(true)
  const [submitError, setSubmitError] = useState<Error>()
  const [isRejectedByUser, setIsRejectedByUser] = useState(false)

  // Transaction state
  const [currentStep, setCurrentStep] = useState<ApprovalStep | undefined>(undefined)
  const [localTxId, setLocalTxId] = useState<string | undefined>(existingTxId)
  const [safeTxHash, setSafeTxHash] = useState<string>()
  const [isCalculatingHash, setIsCalculatingHash] = useState(false)

  // Effective txId: use existing (for proposed tx) or local (after propose)
  const txId = existingTxId ?? localTxId

  // Contexts and hooks
  const { safeTx } = useContext(SafeTxContext)
  const { setTxFlow } = useContext(TxModalContext)
  const { proposeTx, approveHash, executeApprovedTx } = useHederaTxActions()
  const isSafeOwner = useIsSafeOwner()

  // Gas estimation based on current operation
  const [gasEstimate] = useHederaGas(currentStep === 'execute' ? 'execTransaction' : 'approveHash')

  // Check approval status on-chain (only when we have the hash and not in propose step)
  const { approvalStatus } = useHederaApprovalStatus(safeTxHash, safeTx, !!safeTxHash && currentStep !== 'propose')

  // Calculate safeTxHash from safeTx when available
  useEffect(() => {
    const calculateHash = async () => {
      if (!safeTx) return

      setIsCalculatingHash(true)
      try {
        const safeSDK = getAndValidateSafeSDK()
        const hash = await safeSDK.getTransactionHash(safeTx)
        setSafeTxHash(hash)
      } catch (err) {
        console.error('Error calculating safeTxHash:', err)
        setSubmitError(asError(err))
      } finally {
        setIsCalculatingHash(false)
      }
    }

    calculateHash()
  }, [safeTx])

  // Determine initial step based on context
  useEffect(() => {
    // Wait until we have the hash calculated
    if (!safeTxHash) return

    // Only set initial step once
    if (currentStep !== undefined) return

    if (isCreation) {
      // New transaction: start with propose
      setCurrentStep('propose')
    } else {
      // Existing transaction: wait for approvalStatus to be loaded before determining step
      // This prevents incorrectly going to 'approve' when the tx is actually ready to execute
      if (approvalStatus === undefined) {
        // Still loading approval status, wait
        return
      }

      if (approvalStatus.isReady) {
        setCurrentStep('execute')
      } else {
        setCurrentStep('approve')
      }
    }
  }, [isCreation, safeTxHash, approvalStatus, currentStep])

  // NOTE: We intentionally do NOT auto-transition from approve to execute.
  // After approveHash, the user is redirected to SuccessScreen and must
  // return to the transaction queue to execute. This ensures the safeTx
  // is reloaded with the correct nonce from the CGW.

  // Permissions
  const canPropose = isCreation
  const canApprove = isSafeOwner
  const canExecute = approvalStatus?.isReady ?? false

  // Handler for proposing transaction to the gateway
  const handlePropose = useCallback(async () => {
    if (!safeTx) return

    setIsSubmittable(false)
    setSubmitError(undefined)
    setIsRejectedByUser(false)

    try {
      const proposedTxId = await proposeTx(safeTx, origin)
      setLocalTxId(proposedTxId)
      setCurrentStep('approve')

      // If not a Safe owner, close the modal after proposing
      if (!isSafeOwner) {
        onSubmit?.(proposedTxId, false)
        setTxFlow(undefined)
      }
    } catch (err) {
      const error = asError(err)
      if (isWalletRejection(error)) {
        setIsRejectedByUser(true)
      } else {
        trackError(Errors._810, error)
        setSubmitError(error)
      }
    } finally {
      setIsSubmittable(true)
    }
  }, [safeTx, proposeTx, origin, isSafeOwner, onSubmit, setTxFlow])

  // Handler for approving the transaction hash on-chain
  const handleApprove = useCallback(async () => {
    if (!safeTxHash || !txId) return

    setIsSubmittable(false)
    setSubmitError(undefined)
    setIsRejectedByUser(false)

    // Store current confirmation count before approval
    const initialConfirmations = approvalStatus?.approvalCount ?? 0

    try {
      const txHash = await approveHash(safeTxHash, txId)
      setCurrentStep('done')

      onSubmit?.(txId, false) // false = not executed yet, just approved
      setTxFlow(
        <ApproveHashSuccessScreenFlow txId={txId} txHash={txHash} initialConfirmations={initialConfirmations} />,
        undefined,
        false,
      )
    } catch (err) {
      const error = asError(err)
      if (isWalletRejection(error)) {
        setIsRejectedByUser(true)
      } else {
        trackError(Errors._811, error)
        setSubmitError(error)
      }
    } finally {
      setIsSubmittable(true)
    }
  }, [safeTxHash, txId, approveHash, approvalStatus?.approvalCount, onSubmit, setTxFlow])

  // Handler for executing the transaction on-chain
  const handleExecute = useCallback(async () => {
    if (!safeTx || !txId) return

    setIsSubmittable(false)
    setSubmitError(undefined)
    setIsRejectedByUser(false)

    try {
      await executeApprovedTx(safeTx, txId)
      setCurrentStep('done')

      onSubmit?.(txId, true)
      setTxFlow(<SuccessScreenFlow txId={txId} />, undefined, false)
    } catch (err) {
      const error = asError(err)
      if (isWalletRejection(error)) {
        setIsRejectedByUser(true)
      } else {
        trackError(Errors._812, error)
        setSubmitError(error)
      }
    } finally {
      setIsSubmittable(true)
    }
  }, [safeTx, txId, executeApprovedTx, onSubmit, setTxFlow])

  // Button configuration based on current step
  const buttonConfig = useMemo(() => {
    switch (currentStep) {
      case 'propose':
        return {
          label: isSafeOwner ? 'Propose transaction' : 'Propose (non-owner)',
          handler: handlePropose,
          disabled: !canPropose,
        }
      case 'approve':
        return {
          label: 'Approve hash onchain',
          handler: handleApprove,
          disabled: !canApprove || !safeTxHash || !txId,
        }
      case 'execute':
        return {
          label: 'Execute transaction',
          handler: handleExecute,
          disabled: !canExecute,
        }
      default:
        return null
    }
  }, [
    currentStep,
    isSafeOwner,
    handlePropose,
    canPropose,
    handleApprove,
    canApprove,
    safeTxHash,
    txId,
    handleExecute,
    canExecute,
  ])

  // Loading state while calculating hash or determining initial step
  const isInitializing = isCalculatingHash || currentStep === undefined

  const submitDisabled = !safeTx || !isSubmittable || disableSubmit || isInitializing || buttonConfig?.disabled

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        buttonConfig?.handler()
      }}
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Hedera Approval Process:</strong>
          <br />
          {isInitializing && <>Loading transaction details...</>}
          {currentStep === 'propose' && (
            <>
              This transaction will be proposed to the transaction service. After proposal, you will need to approve it
              on-chain via approveHash.
            </>
          )}
          {currentStep === 'approve' && (
            <>
              Approve this transaction hash on-chain. After approval, you will be redirected and can return to execute
              the transaction once all required approvals are collected. Cost: ~{gasEstimate?.estimatedCostHbar} HBAR.
            </>
          )}
          {currentStep === 'execute' && (
            <>
              This transaction has {approvalStatus?.approvalCount} of {approvalStatus?.threshold} required approvals and
              is ready to execute on-chain.
            </>
          )}
        </Typography>
      </Alert>

      {gasEstimate && currentStep && currentStep !== 'propose' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Estimated cost: {gasEstimate.estimatedCostHbar} HBAR (Gas limit: {gasEstimate.gasLimit.toLocaleString()})
          </Typography>
        </Box>
      )}

      {approvalStatus && currentStep && currentStep !== 'propose' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            Approvals: {approvalStatus.approvalCount} / {approvalStatus.threshold}
            {approvalStatus.isReady && ' - Ready to execute'}
          </Typography>
        </Box>
      )}

      {submitError && (
        <ErrorMessage error={submitError}>Error processing Hedera transaction. Please try again.</ErrorMessage>
      )}

      {isRejectedByUser && (
        <Box mt={1}>
          <WalletRejectionError />
        </Box>
      )}

      {!isSafeOwner && currentStep === 'approve' && (
        <ErrorMessage level="warning">Only Safe owners can approve transaction hashes.</ErrorMessage>
      )}

      <Divider className={commonCss.nestedDivider} sx={{ pt: 3 }} />

      <CardActions>
        <Stack
          sx={{ width: ['100%', '100%', '100%', 'auto'] }}
          direction={{ xs: 'column-reverse', lg: 'row' }}
          spacing={{ xs: 2, md: 2 }}
        >
          <CheckWallet checkNetwork>
            {(isOk) => (
              <Button
                data-testid="hedera-approve-execute-btn"
                variant="contained"
                type="submit"
                disabled={!isOk || submitDisabled}
                sx={{ minWidth: '162px', order: '1', width: ['100%', '100%', '100%', 'auto'] }}
              >
                {!isSubmittable || isInitializing ? <CircularProgress size={20} /> : buttonConfig?.label || 'Continue'}
              </Button>
            )}
          </CheckWallet>
        </Stack>
      </CardActions>
    </form>
  )
}

export default HederaApproveExecuteForm
